import { AssetManager, SpineExtended, container } from '@game-studio/arcade-base';
import { ISkeletonData } from 'pixi-spine';
import { Container, Graphics, Rectangle, Text, Texture } from 'pixi.js-legacy';
import { symbolService } from '../../services/symbol-service';
import { collectorService } from '../../services/collector-service';
import gsap from 'gsap';
import { GameState, GoldState, gameModel } from '../../models/game-model';
import { gameSettings } from '../../models/game-settings';
import { collectorKey } from './collector/collector-view';
import { soundService } from '../../services/sound-service';
import { optimizationService } from '../../services/optimization-service';
import { gameEventEmitter } from '@/src/events';
import { ISkinConfigEx } from '@/src/types';

export class SymbolView extends Container {
    private _symbolSpine: SpineExtended;
    private _valueText: Text = new Text('');
    private _boundBox: Graphics;

    private _id: number;
    private _isSpecial: boolean;

    private _removalDelay: gsap.core.Tween;

    constructor(id: number) {
        super();
        // Set symbol id.
        this._id = id;

        // Create spine asset.
        const spineData: ISkeletonData = AssetManager.getSpineResourceData('symbols');
        this._symbolSpine = new SpineExtended(spineData);
        this.alpha = 0;
        this.addChild(this._symbolSpine);

        // Setup spine state.
        this._symbolSpine.state.setAnimation(0, 'idle', true);

        this.buildSizer();
        this.addInteractivity();

        this._symbolSpine.visible = false;

        // Add bindings and listeners.
        this.addListeners();
    }

    public reveal(): void {
        // Try to allocate a symbol
        const allocation = symbolService.allocate(this._id);

        if (!allocation) {
            console.warn('symbol can not be allocated at this time', this._id);
            return;
        }

        // Allocation successful - set symbol states
        this.interactive = false;
        this._symbolSpine.skeleton.setSkinByName(collectorKey[allocation.allocatedSymbol]);

        // Calculate the prize value
        const prizeValue = container.stake.currentStake * gameSettings.winMultipliers[allocation.allocatedSymbol + 1];
        const prizeText = container.currencyFormatter.precise(prizeValue);

        // Update the prize display
        const slot = this._symbolSpine.skeleton.findSlot('collector/value');
        slot.currentSprite.texture = this.renderPrize(prizeText);

        optimizationService.optimizeOff(this._symbolSpine);
        
        if (allocation.goldenPrize) {
            const timeline_reveal: number = 4000;
            const timeline_updateStates: number = 6200;
            const timeline_remove: number = 8800;
            const sound_timeline_goldenReveal: number = 2100;

            // Special handling for a golden prize.
            this._isSpecial = true;
            this._symbolSpine.state.setAnimation(1, 'spotlight', false);
            soundService.playInstantPrizeAlert();

            // Reveal.
            setTimeout(() => {
                this._symbolSpine.state.setAnimation(0, 'golden', false);
                soundService.playInstantPrizeBegin();
                setTimeout(() => {
                    soundService.playGoldenReveal();
                }, sound_timeline_goldenReveal);
            }, timeline_reveal);

            // Set states and collect.
            setTimeout(() => {
                gameModel.goldState = GoldState.TRANS_OFF;
                collectorService.collectSymbol(allocation);
                soundService.playInstantPrizeComplete();
            }, timeline_updateStates);

            // Remove symbol and update states.
            setTimeout(() => {
                this._symbolSpine.setAnimationPromise('closed').then(() => {
                    this._symbolSpine.visible = false;
                });
                gsap.to(this, {
                    duration: 0.5,
                    onComplete: () => {
                        gameModel.gameState = GameState.IN_PLAY;
                        gameModel.goldState = GoldState.COMPLETE;
                    }
                });
            }, timeline_remove);
        } else {
            const timeline_updateStates: number = 1000;
            const timeline_remove: number = 2; // 2000ms

            // Handling for normal symbol.
            this._symbolSpine.state.setAnimation(0, 'reveal2', false);
            soundService.playSymbolReveal();

            // Fill collector.
            setTimeout(() => {
                collectorService.collectSymbol(allocation);
            }, timeline_updateStates);

            // Remove symbol.
            this._removalDelay = gsap.delayedCall(timeline_remove, () => {
                this._symbolSpine.setAnimationPromise('closed').then(() => {
                    this._symbolSpine.visible = false;
                });
            });
        }
    }
    private buildSizer(): void {
        // Create a bounding box to surround content
        this._boundBox = new Graphics();
        this.addChild(this._boundBox);

        // Set the bounding box properties
        this._boundBox.beginFill(0xffffff, 0);
        this._boundBox.drawRect(0, 0, this._symbolSpine.width, this._symbolSpine.height);
    }

    private renderPrize(value: string): Texture {
        // Create a text object with the specified value and style
        this._valueText = new Text(value);
        this._valueText.style = (container.skin.config as ISkinConfigEx).caseFontStyle;
        this._valueText.updateText(true); // Update the text

        // Return the texture representing the rendered text
        return this._valueText.texture;
    }

    private addInteractivity(): void {
        this.buttonMode = true;
        this.hitArea = new Rectangle(this._boundBox.x, this._boundBox.y, this._boundBox.width, this._boundBox.height);
        this.on('pointerdown', this.reveal, this);
    }

    private onFade$(): void {
        // Fade out normal symbols.
        if (gameModel.goldState === GoldState.LOOP) {
            if (gameModel.symbolStates[this._id] === 0 && !this._isSpecial) {
                gsap.to(this, { alpha: 0.6, duration: 0.5 });
            }
        }
        // Fade in normal symbols.
        if (gameModel.goldState === GoldState.COMPLETE) {
            if (gameModel.symbolStates[this._id] === 0) {
                gsap.to(this, { alpha: 1, duration: 0.5 });
            }
        }
    }

    private onNewGame$(): void {
        // Reset symbol state.
        this._symbolSpine.state.setAnimation(0, 'idle', false);
        optimizationService.optimizeOn(this._symbolSpine);
        this._isSpecial = false;

        // Kill all gsap tweens.
        if (this._removalDelay) {
            this._removalDelay.kill();
        }
        gsap.killTweensOf(this);

        // Alpha symbol on.
        this.alpha = 0;
        this._symbolSpine.visible = true;
        const interactivityDelay = 500;
        gsap.to(this, { alpha: 1, duration: 0.5 });
        setTimeout(() => {
            this.interactive = true;
        }, interactivityDelay);
    }

    private addListeners(): void {
        gameModel.bindToValue(
            this,
            () => gameModel.goldState,
            () => this.onFade$
        );
        gameEventEmitter.on('NEW_GAME', this.onNewGame$.bind(this));
    }
}
